import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Users, ClipboardList, Rocket, TrendingUp, Wifi, WifiOff } from 'lucide-react'
import api from '../lib/api'
import { useAgent } from 'agents/react'
import { AGENT_WORKER_HOST, isDev } from '../lib/config'

// Type for agent state returned from the server
interface AgentState {
  id: string
  name: string
  status: string
  lastActivity: string
  metadata: Record<string, unknown>
}

interface Task {
  id: string
  type: string
  agentId: string
  status: string
}

interface Deployment {
  id: string
  projectId: string
  branch: string
  status: string
}

interface ListResponse<T> {
  data: T[]
}

export default function Dashboard() {
  // ─── REST data queries ───────────────────────────────────────────────
  const { data: agents } = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get<ListResponse<AgentState>>('/agent/list').then(res => res.data)
  })

  const { data: tasks } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.get<ListResponse<Task>>('/task/list').then(res => res.data)
  })

  const { data: deployments } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<ListResponse<Deployment>>('/deploy/list').then(res => res.data)
  })

  // ─── Real-time agent connection status via WebSocket ─────────────────
  // In production, connect directly to the Worker host.
  // In development, use the Vite proxy (same origin).
  const agentHost = isDev ? undefined : AGENT_WORKER_HOST

  const chatAgent = useAgent<AgentState>({
    agent: 'chat-agent',
    name: 'default',
    host: agentHost,
  })

  const orchAgent = useAgent<AgentState>({
    agent: 'orchestrator-agent',
    name: 'default',
    host: agentHost,
  })

  const agentConnections = [
    { name: 'ChatAgent', agent: chatAgent },
    { name: 'OrchestratorAgent', agent: orchAgent },
  ]

  // ─── Stats ───────────────────────────────────────────────────────────
  const successRate = useMemo(() => {
    if (!tasks?.data) return '0%'
    const completed = tasks.data.filter((t: Task) => t.status === 'completed').length
    const total = tasks.data.length
    return total > 0 ? `${((completed / total) * 100).toFixed(1)}%` : '0%'
  }, [tasks])

  const stats = [
    {
      label: 'Total Agents',
      value: agents?.data?.length || 0,
      icon: Users,
      color: 'text-blue-500'
    },
    {
      label: 'Active Tasks',
      value: tasks?.data?.filter((t: any) => t.status === 'running').length || 0,
      icon: ClipboardList,
      color: 'text-green-500'
    },
    {
      label: 'Deployments',
      value: deployments?.data?.length || 0,
      icon: Rocket,
      color: 'text-orange-500'
    },
    {
      label: 'Success Rate',
      value: successRate,
      icon: TrendingUp,
      color: 'text-purple-500'
    }
  ]

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard</h2>

      {/* Agent Connection Status */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 mb-6">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Agent Connections</h3>
        <div className="flex flex-wrap gap-4">
          {agentConnections.map(({ name, agent }) => (
            <div key={name} className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg">
              {agent.identified ? (
                <Wifi className="w-4 h-4 text-green-400" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-400" />
              )}
              <span className="text-sm font-medium">{name}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                agent.identified ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
              }`}>
                {agent.identified ? 'connected' : 'disconnected'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <Icon className={`w-8 h-8 ${color}`} />
              <span className="text-3xl font-bold">{value}</span>
            </div>
            <p className="text-gray-400 text-sm">{label}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Recent Tasks</h3>
          {tasks?.data?.slice(0, 5).map((task: Task) => (
            <div key={task.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
              <div>
                <p className="text-sm font-medium">{task.type}</p>
                <p className="text-xs text-gray-400">{task.agentId}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                task.status === 'completed' ? 'bg-green-900 text-green-300' :
                task.status === 'running' ? 'bg-blue-900 text-blue-300' :
                task.status === 'failed' ? 'bg-red-900 text-red-300' :
                'bg-gray-700 text-gray-300'
              }`}>
                {task.status}
              </span>
            </div>
          ))}
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Recent Deployments</h3>
          {deployments?.data?.slice(0, 5).map((deploy: Deployment) => (
            <div key={deploy.id} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
              <div>
                <p className="text-sm font-medium">{deploy.projectId}</p>
                <p className="text-xs text-gray-400">{deploy.branch}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs ${
                deploy.status === 'completed' ? 'bg-green-900 text-green-300' :
                deploy.status === 'deploying' ? 'bg-blue-900 text-blue-300' :
                deploy.status === 'failed' ? 'bg-red-900 text-red-300' :
                'bg-gray-700 text-gray-300'
              }`}>
                {deploy.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
